import React, { useEffect, useMemo, useState } from 'react';
import {
  FaClipboardList,
  FaDownload,
  FaExclamationTriangle,
  FaFileInvoiceDollar,
  FaProjectDiagram,
  FaSearch,
} from 'react-icons/fa';
import { formatCurrency } from '../utils/format';
import { sortNewestFirst } from '../utils/newestFirst';
import { getOrderFinancialBreakdown } from '../utils/orderCheckoutCharges';
import { getOrderStatusDisplay, getStatusLabel } from '../utils/orderStatusLabel';
import { isCreatedToday } from '../utils/reportRange';
import DeliveryOrderSummary from './DeliveryOrderSummary';

export interface OperationOrderItem {
  menu_item_id: string;
  quantity: number;
  name?: string;
  note?: string;
  customizations?: Record<string, string>;
}

export interface OperationOrder {
  id: string;
  order_number?: string;
  cafe_id: string;
  cafe_name: string;
  cafe_address: string;
  user_name: string;
  user_email: string;
  user_phone?: string | null;
  items: OperationOrderItem[];
  total_amount: number;
  points_earned: number;
  points_used: number;
  student_discount?: number;
  platform_fee_jod?: number | null;
  refunded_jod?: number | null;
  status: string;
  name?: string;
  payment_method?: string;
  wallet_used?: number;
  promotion_discount?: number;
  promotion_id?: string | null;
  merchandise_subtotal_jod?: number | null;
  points_discount_jod?: number | null;
  fulfillment_type?: string | null;
  jordan_tax_jod?: number | null;
  service_fee_jod?: number | null;
  small_order_fee_jod?: number | null;
  delivery_fee_jod?: number | null;
  fees_jod?: number | null;
  created_at: string;
  status_updated_at?: string | null;
  ready_at?: string | null;
  completed_at?: string | null;
  cancelled_at?: string | null;
  cancelled_from_status?: string | null;
  delivery_address?: string | null;
  delivery_distance_km?: number | null;
  delivery_duration_min?: number | null;
  table_number?: number | null;
  dine_in_discount_jod?: number | null;
  dine_in_discount_percent?: number | null;
  dine_in_commission_jod?: number | null;
  dine_in_commission_charged?: number | boolean | null;
}

export type HistoryFilter =
  | 'all'
  | 'completed'
  | 'cancelled'
  | 'active'
  | 'in_progress'
  | 'pending'
  | 'received'
  | 'preparing'
  | 'ready';

function orderStatusKey(status: string): string {
  return String(status || '').toLowerCase().trim();
}

function OrderStatusBadge({
  order,
  getStatusColor,
  className = 'px-2 py-0.5 rounded text-xs font-semibold border',
}: {
  order: OperationOrder;
  getStatusColor: (status: string) => string;
  className?: string;
}) {
  const display = getOrderStatusDisplay(order);
  return (
    <span className={`inline-block ${className} ${getStatusColor(order.status)}`}>
      {display.fullLabel}
    </span>
  );
}

interface OrderOperationsPanelProps {
  orders: OperationOrder[];
  getStatusColor: (status: string) => string;
  getStatusButtons: (currentStatus: string, orderId: string) => React.ReactNode;
  onNavigateToReports: () => void;
  onOpenOrderForAction?: (order: OperationOrder) => void;
  requestedHistoryFilter?: HistoryFilter | null;
  onClearRequestedHistoryFilter?: () => void;
  onSelectHistoryFilter?: (filter: HistoryFilter) => void;
  pipelineCounts?: Record<string, number> | null;
  dateScope?: 'today' | 'all';
  onDateScopeChange?: (scope: 'today' | 'all') => void;
}

export default function OrderOperationsPanel({
  orders,
  getStatusColor,
  getStatusButtons,
  onNavigateToReports,
  onOpenOrderForAction,
  requestedHistoryFilter,
  onClearRequestedHistoryFilter,
  onSelectHistoryFilter,
  pipelineCounts,
  dateScope = 'today',
  onDateScopeChange,
}: OrderOperationsPanelProps) {
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>('all');
  const [detailOrder, setDetailOrder] = useState<OperationOrder | null>(null);
  const [historySearch, setHistorySearch] = useState('');
  const statusOnlyFilter = ['pending', 'in_progress', 'received', 'preparing', 'ready'].includes(
    historyFilter
  );

  useEffect(() => {
    if (!requestedHistoryFilter) return;
    setHistoryFilter(requestedHistoryFilter);
  }, [requestedHistoryFilter]);

  const pipeline = useMemo(() => {
    if (pipelineCounts) {
      return {
        pending: Number(pipelineCounts.pending) || 0,
        inProgress: (Number(pipelineCounts.received) || 0) + (Number(pipelineCounts.preparing) || 0),
        ready: Number(pipelineCounts.ready) || 0,
        completed: Number(pipelineCounts.completed) || 0,
      };
    }
    const pending = orders.filter((o) => orderStatusKey(o.status) === 'pending').length;
    const inProgress = orders.filter((o) =>
      ['received', 'preparing'].includes(orderStatusKey(o.status))
    ).length;
    const ready = orders.filter((o) => orderStatusKey(o.status) === 'ready').length;
    const completed = orders.filter((o) => orderStatusKey(o.status) === 'completed').length;
    return { pending, inProgress, ready, completed };
  }, [orders, pipelineCounts]);

  const scheduledOrNew = useMemo(
    () => orders.filter((o) => orderStatusKey(o.status) === 'pending'),
    [orders]
  );

  const historyRows = useMemo(() => {
    let list = [...orders];
    if (historyFilter === 'completed') {
      list = list.filter((o) => orderStatusKey(o.status) === 'completed');
    } else if (historyFilter === 'cancelled') {
      list = list.filter((o) => orderStatusKey(o.status) === 'cancelled');
    } else if (historyFilter === 'active') {
      list = list.filter((o) => !['completed', 'cancelled'].includes(orderStatusKey(o.status)));
    } else if (historyFilter === 'in_progress') {
      list = list.filter((o) => ['received', 'preparing'].includes(orderStatusKey(o.status)));
    } else if (statusOnlyFilter) {
      list = list.filter((o) => orderStatusKey(o.status) === historyFilter);
    }

    if (dateScope === 'today') {
      list = list.filter((o) => isCreatedToday(o.created_at));
    }

    const q = historySearch.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (o) =>
          (o.order_number || o.id).toLowerCase().includes(q) ||
          o.user_name.toLowerCase().includes(q) ||
          o.user_email.toLowerCase().includes(q) ||
          o.cafe_name.toLowerCase().includes(q)
      );
    }

    return sortNewestFirst(list, (order) => order.created_at);
  }, [orders, historyFilter, historySearch, statusOnlyFilter, dateScope]);

  const activeOrdersForUpdate = useMemo(
    () =>
      sortNewestFirst(
        orders.filter((o) => !['completed', 'cancelled'].includes(o.status)),
        (order) => order.created_at
      ),
    [orders]
  );

  const filterChips: { id: HistoryFilter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'active', label: 'Active' },
    { id: 'completed', label: 'Completed' },
    { id: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div className="space-y-8">
      {/* Order History intro */}
      <section id="orders-history-panel" className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-bold text-amber-800 flex items-center gap-2">
              <FaClipboardList />
              Order history
            </h2>
            <p className="text-sm text-gray-600 mt-1 max-w-3xl">
              Today&apos;s orders first. Switch to All dates to review older tickets, or filter by
              status and search by order #, customer, or café.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          {(
            [
              { id: 'today' as const, label: 'Today' },
              { id: 'all' as const, label: 'All dates' },
            ]
          ).map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onDateScopeChange?.(c.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                dateScope === c.id
                  ? 'bg-[#3d2918] text-[#fff8f2]'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {filterChips.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                setHistoryFilter(c.id);
                onSelectHistoryFilter?.(c.id);
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                historyFilter === c.id
                  ? 'bg-amber-700 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        {statusOnlyFilter && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            Showing only <span className="font-semibold capitalize">{getStatusLabel(historyFilter)}</span> orders.
            <button
              type="button"
              onClick={() => {
                setHistoryFilter('all');
                onClearRequestedHistoryFilter?.();
              }}
              className="ml-2 font-semibold underline underline-offset-2"
            >
              Clear
            </button>
          </div>
        )}

        <div className="relative mb-4">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="search"
            placeholder="Search order #, customer, cafe…"
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none"
          />
        </div>

        {scheduledOrNew.length > 0 && (
          <p className="text-xs text-gray-500 mb-3">
            <span className="font-semibold text-gray-700">New / queued:</span>{' '}
            {scheduledOrNew.length} order(s) in &ldquo;New&rdquo;—advance to{' '}
            <span className="font-medium">In progress</span> when preparation starts.
          </p>
        )}

        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="text-left py-2 px-3">Order</th>
                <th className="text-left py-2 px-3">When</th>
                <th className="text-left py-2 px-3">Customer</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-right py-2 px-3">Total</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {historyRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">
                    {dateScope === 'today' && historyFilter === 'all'
                      ? 'No orders today. Switch to All dates to see older tickets.'
                      : historyFilter === 'all'
                        ? 'No orders match this view.'
                        : `No ${getStatusLabel(historyFilter).toLowerCase()} orders in this view.`}
                  </td>
                </tr>
              ) : (
                historyRows.map((order) => (
                  <tr key={order.id} className="border-t border-gray-100 hover:bg-amber-50/40">
                    <td className="py-2 px-3 font-medium text-amber-900">
                      <button
                        type="button"
                        onClick={() => onOpenOrderForAction?.(order)}
                        className="text-amber-900 hover:text-amber-700 hover:underline underline-offset-2"
                        title="Open this order in the action panel"
                      >
                        {order.order_number || `#${order.id.slice(0, 8)}`}
                      </button>
                    </td>
                    <td className="py-2 px-3 text-gray-600 whitespace-nowrap">
                      {new Date(order.created_at).toLocaleString()}
                    </td>
                    <td className="py-2 px-3 text-gray-700">
                      <div>{order.user_name}</div>
                      {order.user_phone && (
                        <div className="text-xs text-gray-500">📞 {order.user_phone}</div>
                      )}
                      <div className="text-xs text-gray-500">{order.cafe_name}</div>
                    </td>
                    <td className="py-2 px-3">
                      <OrderStatusBadge order={order} getStatusColor={getStatusColor} />
                    </td>
                    <td className="py-2 px-3 text-right font-semibold text-gray-900">
                      {formatCurrency(order.total_amount)}
                    </td>
                    <td className="py-2 px-3 text-right">
                      <button
                        type="button"
                        onClick={() => setDetailOrder(order)}
                        className="text-amber-700 font-semibold text-xs hover:underline"
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Order status tracking */}
      <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <h2 className="text-xl font-bold text-amber-800 flex items-center gap-2 mb-2">
          <FaProjectDiagram />
          Order status tracking
        </h2>
        <p className="text-sm text-gray-600 mb-6 max-w-3xl">
          Keep work flowing: Queued → In progress → Ready for pickup → Completed.
          These counts are for every order in this café scope, not only the rows on this page.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Queued', count: pipeline.pending, color: 'border-yellow-400 bg-yellow-50', filter: 'pending' as HistoryFilter },
            {
              label: 'In progress',
              count: pipeline.inProgress,
              color: 'border-purple-400 bg-purple-50',
              filter: 'in_progress' as HistoryFilter,
            },
            { label: 'Ready for pickup', count: pipeline.ready, color: 'border-green-400 bg-green-50', filter: 'ready' as HistoryFilter },
            {
              label: 'Completed',
              count: pipeline.completed,
              color: 'border-gray-400 bg-gray-50',
              filter: 'completed' as HistoryFilter,
            },
          ].map((step) => (
            <button
              key={step.label}
              type="button"
              onClick={() => {
                setHistoryFilter(step.filter);
                onSelectHistoryFilter?.(step.filter);
              }}
              className={`w-full text-left rounded-lg p-4 border-l-4 ${step.color} shadow-sm transition hover:brightness-[0.98]`}
            >
              <div className="text-2xl font-bold text-gray-900">{step.count}</div>
              <div className="text-sm font-medium text-gray-700">{step.label}</div>
            </button>
          ))}
        </div>
      </section>

      {/* Order errors report CTA */}
      <section className="bg-gray-900 text-white rounded-lg shadow-md p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex gap-3">
            <FaExclamationTriangle className="text-amber-400 text-2xl flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-lg font-bold">Order errors (transaction) report</h2>
              <p className="text-sm text-gray-300 mt-1 max-w-xl">
                Export issue types, cancellations, and adjustments affecting payouts. Full
                CSV lives under{' '}
                <span className="text-amber-300 font-semibold">Reports</span> in the sidebar.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onNavigateToReports}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-gray-900 font-semibold rounded-lg text-sm"
          >
            <FaDownload />
            Open reports
          </button>
        </div>
      </section>

      {/* Active order actions (same as home) */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4">Update active orders</h2>
        <p className="text-sm text-gray-600 mb-4">
          Advance statuses or cancel from here—the same controls as on Home, newest first.
        </p>
        {activeOrdersForUpdate.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-600">
            No active orders right now.
          </div>
        ) : (
          <div className="space-y-4">
            {activeOrdersForUpdate.map((order) => (
              <div
                key={order.id}
                className="bg-white rounded-lg shadow-md p-5 border-l-4 border-amber-500"
              >
                  <div className="flex flex-wrap justify-between gap-2 mb-2">
                    <div>
                      <span className="font-bold text-amber-900">
                        {order.order_number || `Order #${order.id.slice(0, 8)}`}
                      </span>
                      <span className="ml-2">
                        <OrderStatusBadge order={order} getStatusColor={getStatusColor} />
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDetailOrder(order)}
                      className="text-sm text-amber-700 font-semibold hover:underline"
                    >
                      Transaction details
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    {new Date(order.created_at).toLocaleString()} · {order.cafe_name}
                  </p>
                {getStatusButtons(order.status, order.id)}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Detail modal */}
      {detailOrder && (
        <OrderDetailModal
          order={detailOrder}
          onClose={() => setDetailOrder(null)}
          getStatusColor={getStatusColor}
        />
      )}
    </div>
  );
}

function OrderDetailModal({
  order,
  onClose,
  getStatusColor,
}: {
  order: OperationOrder;
  onClose: () => void;
  getStatusColor: (status: string) => string;
}) {
  const fin = getOrderFinancialBreakdown(order);
  const statusDisplay = getOrderStatusDisplay(order);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-detail-title"
    >
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex justify-between items-center">
          <h3 id="order-detail-title" className="text-lg font-bold text-gray-900">
            Order details
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 text-xl leading-none px-2"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div className="flex flex-wrap justify-between gap-2">
            <span className="font-semibold text-amber-900">
              {order.order_number || `Order #${order.id.slice(0, 8)}`}
            </span>
            <OrderStatusBadge order={order} getStatusColor={getStatusColor} />
          </div>
          {statusDisplay.stageLabel && (
            <p className="text-gray-600">
              <span className="font-medium text-gray-800">Cancelled while:</span>{' '}
              {statusDisplay.stageLabel}
            </p>
          )}
          <p className="text-gray-600">
            <span className="font-medium text-gray-800">Placed:</span>{' '}
            {new Date(order.created_at).toLocaleString()}
          </p>
          <p className="text-gray-600">
            <span className="font-medium text-gray-800">Customer:</span> {order.user_name} (
            {order.user_email})
          </p>
          {order.user_phone && (
            <p className="text-gray-600">
              <span className="font-medium text-gray-800">Phone:</span> {order.user_phone}
            </p>
          )}
          <p className="text-gray-600">
            <span className="font-medium text-gray-800">Cafe:</span> {order.cafe_name}
          </p>
          <DeliveryOrderSummary order={order} className="text-sm text-amber-900" />
          {order.payment_method && (
            <p className="text-gray-600">
              <span className="font-medium text-gray-800">Payment:</span> {order.payment_method}
            </p>
          )}

          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 font-semibold text-gray-800 flex items-center gap-2">
              <FaFileInvoiceDollar className="text-amber-600" />
              Transaction & payout (reconciliation)
            </div>
            <dl className="divide-y text-gray-700">
              <div className="flex justify-between px-3 py-2">
                <dt>Subtotal</dt>
                <dd className="font-medium">{formatCurrency(fin.subtotal)}</dd>
              </div>
              {fin.promotionDiscount > 0 && (
                <div className="flex justify-between px-3 py-2">
                  <dt>Promotion discount</dt>
                  <dd className="font-medium">-{formatCurrency(fin.promotionDiscount)}</dd>
                </div>
              )}
              {fin.dineInDiscount > 0 && (
                <div className="flex justify-between px-3 py-2">
                  <dt>Dine-in discount</dt>
                  <dd className="font-medium">-{formatCurrency(fin.dineInDiscount)}</dd>
                </div>
              )}
              {Number(order.dine_in_commission_jod) > 0 && (
                <div className="flex justify-between px-3 py-2">
                  <dt>Platform commission (recorded, not collected)</dt>
                  <dd className="font-medium">{formatCurrency(Number(order.dine_in_commission_jod))}</dd>
                </div>
              )}
              <div className="flex justify-between px-3 py-2">
                <dt>Points redeemed</dt>
                <dd className="font-medium">
                  {fin.pointsUsed > 0 ? `${fin.pointsUsed} points` : '—'}
                </dd>
              </div>
              <div className="flex justify-between px-3 py-2">
                <dt>Taxes</dt>
                <dd className="font-medium">
                  {fin.configured ? formatCurrency(fin.tax) : 'Not configured'}
                </dd>
              </div>
              <div className="flex justify-between px-3 py-2">
                <dt>Fees</dt>
                <dd className="font-medium">
                  {fin.configured ? formatCurrency(fin.fees) : 'Not configured'}
                </dd>
              </div>
              <div className="flex justify-between px-3 py-2 bg-amber-50 font-bold text-amber-900">
                <dt>Net order total (customer paid)</dt>
                <dd>{formatCurrency(fin.netPayout)}</dd>
              </div>
            </dl>
            <p className="text-xs text-gray-500 px-3 py-2 bg-gray-50 border-t">
              Taxes are Jordan sales tax (16%). Fees are the service fee, plus small-order
              or delivery fees when they applied at checkout.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-gray-800 mb-2">Items</h4>
            <ul className="space-y-1 text-gray-600">
              {order.items.map((item, idx) => (
                <li key={idx}>
                  {item.quantity}× {item.name || item.menu_item_id.slice(0, 8)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
